<template>
  <modal-wrap :id="id" :name="id" :hide-footer="false" :hide-header-close="true" size="md">
    <template #default>
      {{ messageText }}
    </template>
    <template #modal-title>
      {{ titleText }}
    </template>
    <template #modal-footer>
      <div>
        <b-button size="md" class="btn-grey mr-3" @click="cancel"> Cancel </b-button>
        <b-button variant="success" size="md" class="btn-green" @click="confirm()">
          {{ okButtonText }}
        </b-button>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import { BButton } from 'bootstrap-vue-next'
import { computed, type ComputedRef, defineComponent, onMounted, onUnmounted } from 'vue'
import { registerModals } from '@/services/Modal'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'Confirm',
  components: { BButton, ModalWrap },
  props: {
    id: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      required: false,
      default: '',
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    onConfirm: {
      type: Function,
      required: false,
      default: null,
    },
    onCancel: {
      type: Function,
      required: false,
      default: null,
    },
  },
  setup(props) {
    onMounted(() => {
      registerModals(props.id)
      window.addEventListener('keydown', handleKeyDown)
    })
    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeyDown)
    })
    const modalStore = useVueNextModalStore()
    const modalData = computed(() => modalStore.modalPropsByName(props.id))

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        confirm()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        cancel()
      }
    }

    const isOpened: ComputedRef<boolean> = computed(() => modalStore.isModalVisible(props.id))
    const messageText = computed(() => props.message || modalData.value?.message || '')
    const titleText = computed(() => props.title || modalData.value?.title || '')
    const okButtonText = computed(() => modalData.value?.okButtonText || 'Confirm')
    const confirm = () => {
      if (!isOpened.value) {
        return
      }
      const onSubmit = props.onConfirm || modalData.value?.onConfirm || (() => {})
      onSubmit(modalStore.modalPropsByName(props.id))
      modalStore.closeModal(props.id)
    }
    const cancel = () => {
      const onCancel = props.onCancel || modalData.value?.onCancel || (() => {})
      onCancel(modalStore.modalPropsByName(props.id))
      modalStore.closeModal(props.id)
    }

    return {
      okButtonText,
      messageText,
      titleText,
      confirm,
      cancel,
    }
  },
})
</script>
